import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@deskbay/shared/services/supabase/client';
import { logger } from '@deskbay/shared';

/**
 * useDeepLink — Handles deep links on Capacitor native (iOS + Android).
 *
 * Supported URL formats:
 *   Custom scheme:  io.deskbay.app://dashboard/foo     → /dashboard/foo
 *   App Links:      https://app.deskbay.io/dashboard   → /dashboard
 *   Auth callback:  io.deskbay.app://auth/callback#... → OAuth flow
 *   Reset password: .../reset-password?token=...       → /auth/reset-password
 *
 * Only active on native platforms. No-op on web.
 */
export function useDeepLink() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let listenerHandle: { remove: () => void } | null = null;

    const setup = async () => {
      const { App } = await import('@capacitor/app');

      listenerHandle = await App.addListener('appUrlOpen', async ({ url }) => {
        logger.capacitor.info('Received URL', 'DeepLink', { url });

        try {
          // Parse the URL
          let urlObj: URL;
          try {
            urlObj = new URL(url);
          } catch (e) {
            const normalizedUrl = url.includes('://') ? url : `io.deskbay.app://${url}`;
            urlObj = new URL(normalizedUrl);
          }

          const search = urlObj.search || '';

          // Extract the app-relative path depending on URL type:
          // - HTTPS App Links: https://app.deskbay.io/dashboard → pathname = /dashboard
          // - Custom scheme:   io.deskbay.app://dashboard/foo   → host = dashboard, pathname = /foo
          let appPath: string;
          if (urlObj.protocol === 'https:' && urlObj.host === 'app.deskbay.io') {
            // App Links (Android verified, iOS Universal Links)
            appPath = urlObj.pathname || '/';
          } else {
            // Custom scheme: URL() treats the first segment as host
            const host = urlObj.host || '';
            const path = urlObj.pathname || '/';
            appPath = host ? `/${host}${path === '/' ? '' : path}` : path;
          }
          appPath = appPath.startsWith('/') ? appPath : `/${appPath}`;
          const fullPath = appPath.toLowerCase();

          // Handle Supabase OAuth / magic link callback
          if (fullPath.includes('/auth/callback') || url.toLowerCase().includes('auth/callback')) {
            logger.capacitor.info('OAuth callback detected', 'DeepLink');

            // Supabase implicit grant: tokens arrive in the URL hash fragment
            const hashString = url.includes('#') ? url.split('#')[1] : '';
            const hashParams = new URLSearchParams(hashString);
            const accessToken = hashParams.get('access_token');
            const refreshToken = hashParams.get('refresh_token');

            if (accessToken && refreshToken) {
              logger.capacitor.info('Setting session from implicit grant tokens', 'DeepLink');
              const { error: sessionError } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken,
              });

              if (sessionError) {
                logger.capacitor.error('setSession failed', sessionError, 'DeepLink');
              } else {
                logger.capacitor.info('Session set successfully', 'DeepLink');
              }
            } else {
              // PKCE flow fallback — exchange authorization code
              const codeParam = new URLSearchParams(urlObj.search).get('code');
              if (codeParam) {
                logger.capacitor.info('Exchanging PKCE code for session', 'DeepLink');
                const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(codeParam);
                if (exchangeError) {
                  logger.capacitor.error('PKCE exchange failed', exchangeError, 'DeepLink');
                }
              } else {
                logger.capacitor.error('No tokens or code found in callback URL', null, 'DeepLink');
              }
            }

            navigate('/dashboard', { replace: true });
            return;
          }

          // Handle Google Business OAuth callback (native)
          if (fullPath.includes('/google-business/callback')) {
            logger.capacitor.info('Google Business callback detected', 'DeepLink', { url });
            const statusParam = new URLSearchParams(urlObj.search || search).get('status');
            // Navigate to the Google Business settings page with status
            navigate(`/dashboard/profile/google?provider=google&status=${statusParam || 'success'}${search.replace(/^\?/, '&')}`, { replace: true });
            // Close the browser overlay (Android Custom Tabs)
            try {
              const { Browser } = await import('@capacitor/browser');
              await Browser.close();
            } catch (e) { /* Browser might not be open — safe to ignore */ }
            return;
          }

          // Handle password reset
          if (fullPath.includes('reset-password')) {
            navigate(`/auth/reset-password${search}`, { replace: true });
            return;
          }

          // Generic path navigation
          logger.capacitor.info('Navigating to', 'DeepLink', { path: appPath + search });
          navigate(appPath + search, { replace: true });

          // Close any open browser overlay (Android Custom Tabs after Stripe redirect)
          try {
            const { Browser } = await import('@capacitor/browser');
            await Browser.close();
          } catch (e) { /* Browser might not be open — safe to ignore */ }

        } catch (e) {
          logger.capacitor.error('Failed to parse URL', e, 'DeepLink', { url });
        }
      });

      logger.capacitor.info('Listener registered', 'DeepLink');
    };

    setup();

    return () => {
      if (listenerHandle) {
        listenerHandle.remove();
      }
    };
  }, [navigate]);
}
